import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TuiItem } from '@taiga-ui/cdk';
import { TuiAlertService, TuiHint, TuiIcon, TuiScrollbar } from '@taiga-ui/core';
import { TuiExpand } from '@taiga-ui/experimental/components/expand';
import { BehaviorSubject, EMPTY, Observable, of, shareReplay, Subscription, take } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';

import { ContentLoaderComponent } from '@common/components/content-loader/content-loader.component';
import { MaintenanceStatePipe } from '@common/components/create-object/tabs/object-maintenance-tab/utils/pipes/maintenance-state.pipe';
import {
  IObjectCardAdapter,
  OBJECT_CARD_ADAPTER_TOKEN,
} from '@common/components/object-card-adapter';
import { getEnding } from '@common/utils/ending-string-handler';
import { formatDateToRelativeFromISO } from '@common/utils/format-date';
import { CustomDatePipe } from '@common/utils/pipes/custom-date.pipe';
import { JoinPipe } from '@common/utils/pipes/join.pipe';
import { ObjectIconPipe } from '@common/object-icons';
import { SortByPipe } from '@common/utils/pipes/sort-by.pipe';

import {
  ObjectCardBlockKey,
  ObjectCardConnectionSettingsType,
  ObjectCardCustomFieldType,
  ObjectCardDriverType,
  ObjectCardExpandedState,
  ObjectCardGeneralContentType,
  ObjectCardGeneralType,
  ObjectCardGeozoneType,
  ObjectCardKind,
  ObjectCardMaintenanceType,
  ObjectCardParamsType,
  ObjectCardSensorsType,
  ObjectCardSpecificationsType,
  ObjectCardTrailerType,
  ObjectCardType,
  TemplateByCurrentUserSettingsType,
} from './object-card.type';
import {
  getObjectCardBlocksOrder,
  getObjectCardGeneralContent,
  normalizeObjectCardSettings,
} from './object-card.utils';

@Component({
  selector: 'app-object-card',
  templateUrl: './object-card.component.html',
  styleUrls: ['./object-card.component.less'],
  imports: [
    TuiIcon,
    AsyncPipe,
    NgTemplateOutlet,
    TranslatePipe,
    TuiScrollbar,
    TuiExpand,
    TuiItem,
    TuiHint,
    SortByPipe,
    JoinPipe,
    CustomDatePipe,
    ContentLoaderComponent,
    ObjectIconPipe,
    MaintenanceStatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ObjectCardComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  protected readonly currentLang = inject(TranslateService).currentLang;
  private readonly alerts = inject<TuiAlertService>(TuiAlertService);
  private readonly translate = inject(TranslateService);
  private readonly adapter = inject<IObjectCardAdapter>(OBJECT_CARD_ADAPTER_TOKEN);
  private readonly destroyRef = inject(DestroyRef);

  @Input() objectId!: number;
  @Input() accountId: number | null = null;
  @Input() cardType: ObjectCardKind = 'floating';
  @Input() headerActionsTemplate?: TemplateRef<any>;
  /**
   * Карточка на экране. Пока `false`, автообновление данных приостановлено: строка
   * виртуального списка может быть отрендерена в буфере или лежать в кэше вне DOM.
   */
  @Input() set active(value: boolean) {
    this.active$.next(value);
  }
  @Output() objectIntoPopup: any = new EventEmitter<boolean>();

  private readonly active$ = new BehaviorSubject(true);

  @ViewChild('general', { static: true }) generalRef!: TemplateRef<any>;
  @ViewChild('connectionSettings', { static: true }) connectionSettingsRef!: TemplateRef<any>;
  @ViewChild('drivers', { static: true }) driversRef!: TemplateRef<any>;
  @ViewChild('trailers', { static: true }) trailersRef!: TemplateRef<any>;
  @ViewChild('customFields', { static: true }) customFieldsRef!: TemplateRef<any>;
  @ViewChild('sensors', { static: true }) sensorsRef!: TemplateRef<any>;
  @ViewChild('params', { static: true }) paramsRef!: TemplateRef<any>;
  @ViewChild('specifications', { static: true }) specificationsRef!: TemplateRef<any>;
  @ViewChild('geozones', { static: true }) geozonesRef!: TemplateRef<any>;
  @ViewChild('maintenance', { static: true }) maintenanceRef!: TemplateRef<any>;

  settingsTemplates!: TemplateByCurrentUserSettingsType;

  openedCardObjectId: number | null = null;

  getContext(
    objectCard: any,
    settingName: keyof ObjectCardType,
  ): ObjectCardType[keyof ObjectCardType] {
    return objectCard[settingName];
  }

  /** Контекст шаблона блока: данные блока плюс видимые поля general */
  getTemplateContext(
    objectCard: any,
    settingName: ObjectCardBlockKey,
    generalContent: ObjectCardGeneralContentType,
  ) {
    return {
      data: this.getContext(objectCard, settingName),
      fields: generalContent,
    };
  }

  castType<T>(data: any): T {
    return data as T;
  }

  getGeneralType = (data: any): ObjectCardGeneralType => this.castType(data);
  getConnectionSettingsType = (data: any): ObjectCardConnectionSettingsType => this.castType(data);
  getDriversType = (data: any): ObjectCardDriverType[] => this.castType(data);
  getTrailersType = (data: any): ObjectCardTrailerType[] => this.castType(data);
  getCustomFieldType = (data: any): ObjectCardCustomFieldType[] => this.castType(data);
  getSensorsType = (data: any): ObjectCardSensorsType[] => this.castType(data);
  getParamsType = (data: any): ObjectCardParamsType[] | null => this.castType(data);
  getSpecificationsType = (data: any): ObjectCardSpecificationsType[] | null => this.castType(data);
  getGeozoneType = (data: any): ObjectCardGeozoneType[] | null => this.castType(data);
  getMaintenanceType = (data: any): ObjectCardMaintenanceType => this.castType(data);

  protected readonly formatDateToRelative = formatDateToRelativeFromISO;
  protected readonly getEnding = getEnding;

  objectCard$: Observable<ObjectCardType | null> = EMPTY;
  objectCardSettingsFromCurrentUser$: Observable<{
    blocks: ObjectCardBlockKey[];
    generalContent: ObjectCardGeneralContentType;
  }> = EMPTY;
  objectInfo$: Observable<unknown | undefined> = of(undefined);

  expandableBlocks = [
    'drivers',
    'trailers',
    'geozones',
    'sensors',
    'params',
    'connectionSettings',
    'customFields',
    'specifications',
    'maintenance',
  ];

  /** Какие блоки раскрыты. У фиксированных карточек — своё у каждой, у всплывающих — общее. */
  expandedItemsState!: ObjectCardExpandedState;
  isClickableGeozone: boolean = this.adapter.isClickableGeozones;

  get isFixed(): boolean {
    return this.cardType === 'fixed';
  }

  getTemplate = (
    name: keyof Omit<ObjectCardType, 'id' | 'currentUserAccess'>,
  ): TemplateRef<any> | null => {
    return this.settingsTemplates[name] || null;
  };

  getHeaderActionsContext(objectInfo: unknown) {
    return {
      $implicit: objectInfo,
      objectInfo,
      objectId: this.openedCardObjectId,
    };
  }

  private autoRefreshSub: Subscription | null = null;

  ngOnInit() {
    // Потоки и состояние зависят от входов, поэтому собираются здесь, а не в конструкторе
    this.bindToCard();

    // Шаблоны собираем до первого прохода change detection: getTemplate() вызывается
    // из [ngTemplateOutlet], и заполнение карты в ngAfterViewInit давало NG0100.
    this.settingsTemplates = {
      general: this.generalRef,
      drivers: this.driversRef,
      trailers: this.trailersRef,
      geozones: this.geozonesRef,
      sensors: this.sensorsRef,
      params: this.paramsRef,
      connectionSettings: this.connectionSettingsRef,
      customFields: this.customFieldsRef,
      specifications: this.specificationsRef,
      maintenance: this.maintenanceRef,
    };
  }

  /**
   * Компонент переиспользуется с другим объектом: строка виртуального списка получила новый
   * `item`, на карте выбрали другой объект при открытой карточке. Освобождаем прежнюю карточку
   * и перепривязываемся к новой — иначе в строке одного объекта висят данные другого.
   */
  ngOnChanges(changes: SimpleChanges) {
    const objectIdChange = changes['objectId'];
    const cardTypeChange = changes['cardType'];
    const rebind =
      (objectIdChange && !objectIdChange.firstChange) ||
      (cardTypeChange && !cardTypeChange.firstChange);

    if (!rebind) return;

    this.adapter.clearObjectCard(
      objectIdChange?.previousValue ?? this.objectId,
      cardTypeChange?.previousValue ?? this.cardType,
    );
    this.bindToCard();
    this.loadCard();
  }

  ngAfterViewInit() {
    this.loadCard();
  }

  private bindToCard() {
    this.openedCardObjectId = this.objectId;
    this.expandedItemsState = this.adapter.getExpandedItemsState(this.objectId, this.cardType);
    this.objectCard$ = this.adapter.getOpenedObjectCard$(this.objectId, this.cardType);
    this.objectInfo$ = this.adapter.getMonitoringObject$(this.objectId).pipe(take(1));
    this.objectCardSettingsFromCurrentUser$ = this.adapter
      .getCurrentUserObjectCardSettings$(this.cardType)
      .pipe(
        map(normalizeObjectCardSettings),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        map((settings) => ({
          blocks: getObjectCardBlocksOrder(settings),
          generalContent: getObjectCardGeneralContent(settings),
        })),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
  }

  /**
   * Запрашивает данные карточки и подписывается на их обновление по внешним изменениям объекта.
   * Пока карточка не на экране, подписки на обновление нет — уход с экрана отменяет и ожидающий
   * (отложенный адаптером) запрос.
   */
  private loadCard() {
    this.autoRefreshSub?.unsubscribe();

    const objectId = this.objectId;
    const cardType = this.cardType;

    this.adapter.loadObjectCardData(objectId, cardType);

    this.autoRefreshSub = this.active$
      .pipe(
        distinctUntilChanged(),
        switchMap((active) =>
          active ? this.adapter.startAutoRefresh$(objectId, cardType) : EMPTY,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onClickGeozoneName(geozoneId: number) {
    this.adapter.goToGeozoneBounds(geozoneId);
  }

  closePopup() {
    this.objectIntoPopup.emit(null);
    this.adapter.closeObjectCard(this.objectId, this.cardType);
  }

  onCopy(str: string, strAlert: string) {
    navigator.clipboard.writeText(str).then(() => {
      this.alerts
        .open(this.translate.instant(strAlert), {
          autoClose: 5000,
          appearance: 'success',
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    });
  }

  isValidCoordinate(lon: number, lat: number): boolean {
    return isFinite(lon) && lon >= -180 && lon <= 180 && isFinite(lat) && lat >= -90 && lat <= 90;
  }

  onCopyCoordinates(lastMessage: any): void {
    const { y, x } = lastMessage?.pos || {};
    const coordinates = x && y && this.isValidCoordinate(x, y) ? `${y}, ${x}` : null;

    if (!coordinates) return;
    this.onCopy(coordinates, 'Coordinates copied to clipboard');
  }

  onCopyAddress(address: string | null) {
    if (!address) return;
    this.onCopy(address, 'Address copied to clipboard');
  }

  ngOnDestroy() {
    this.autoRefreshSub?.unsubscribe();
    this.adapter.clearObjectCard(this.objectId, this.cardType);
  }
}
